// Plain-language helpers for the multi-provider (OpenAI + Anthropic) parts of the Models tab.
// Everything here treats API fields as untrusted: a value is only used after checking its type.

import type { CheckRunResult } from "@/services/adminModelCurrency";

const NAMES: Record<string, string> = { openai: "OpenAI", anthropic: "Anthropic" };
const PROVIDER_KEYS = ["openai", "anthropic"] as const;
const NO_KEY = "no API key is set";

type Loose = Record<string, unknown>;

function asObject(value: unknown): Loose | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Loose) : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "OpenAI" / "Anthropic"; null for anything else (including missing). */
export function providerName(provider: unknown): string | null {
  return typeof provider === "string" ? (NAMES[provider] ?? null) : null;
}

function modelCountText(status: Loose): string {
  const count = num(status.modelCount);
  return count === null ? "" : ` (${plural(count, "model", "models")})`;
}

function providerSentences(providers: unknown): string[] {
  const all = asObject(providers);
  if (!all) return [];
  const checked: string[] = [];
  const problems: string[] = [];
  for (const key of PROVIDER_KEYS) {
    const status = asObject(all[key]);
    if (!status) continue;
    const name = NAMES[key];
    if (status.skipped === true) problems.push(`${name} was skipped: ${NO_KEY}.`);
    else if (status.ok === true) checked.push(`${name}${modelCountText(status)}`);
    else
      problems.push(
        `Couldn't check ${name}${text(status.error) ? `: ${text(status.error)}` : ""}.`
      );
  }
  const out: string[] = [];
  if (checked.length) out.push(`Checked ${checked.join(" and ")}.`);
  return out.concat(problems);
}

function priceSentences(prices: unknown): string[] {
  const all = asObject(prices);
  if (!all) return [];
  let updated = 0;
  let changed = 0;
  let suspicious = 0;
  const worked: string[] = [];
  const failed: string[] = [];
  for (const key of PROVIDER_KEYS) {
    const status = asObject(all[key]);
    if (!status || status.skipped === true) continue;
    if (status.ok === true) {
      worked.push(NAMES[key]);
      updated += num(status.updated) ?? 0;
      changed += num(status.changed) ?? 0;
      suspicious += num(status.suspicious) ?? 0;
    } else {
      failed.push(NAMES[key]);
    }
  }
  const out: string[] = [];
  if (worked.length) {
    out.push(
      updated > 0
        ? `Prices updated for ${plural(updated, "model", "models")}${changed > 0 ? `, ${changed} changed` : ""}.`
        : "Prices are up to date."
    );
  }
  if (failed.length) {
    out.push(
      worked.length
        ? `Couldn't refresh ${failed.join(" and ")} prices; showing the last known ones.`
        : "Couldn't refresh prices; showing the last known ones."
    );
  }
  if (suspicious > 0) {
    out.push(
      suspicious === 1
        ? "1 price change looks odd and was not applied; a person should look."
        : `${suspicious} price changes look odd and were not applied; a person should look.`
    );
  }
  return out;
}

function newModelsSentence(report: unknown): string {
  const candidates = asObject(report)?.candidates;
  const count = Array.isArray(candidates) ? candidates.length : 0;
  return count > 0 ? `${plural(count, "new model", "new models")} to test.` : "Nothing new.";
}

/** The toast text after "Check now", in plain words. Safe on any shape. */
export function describeCheckResult(result: CheckRunResult | null | undefined | unknown): string {
  const r = asObject(result);
  return [
    ...providerSentences(r?.providers),
    ...priceSentences(r?.prices),
    newModelsSentence(r?.report),
  ].join(" ");
}

/** "OpenAI ✓ 118 models · Anthropic ⚠ not checked: <reason>"; null when there is nothing to say. */
export function providerStatusText(providers: unknown): string | null {
  const all = asObject(providers);
  if (!all) return null;
  const parts: string[] = [];
  for (const key of PROVIDER_KEYS) {
    const status = asObject(all[key]);
    if (!status) continue;
    const name = NAMES[key];
    if (status.skipped === true) parts.push(`${name} ⚠ not checked: ${NO_KEY}`);
    else if (status.ok === true) {
      const count = num(status.modelCount);
      parts.push(`${name} ✓${count === null ? "" : ` ${plural(count, "model", "models")}`}`);
    } else {
      parts.push(`${name} ⚠ not checked: ${text(status.error) ?? "unknown problem"}`);
    }
  }
  return parts.length ? parts.join(" · ") : null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Prices pulled today / yesterday / 3 days ago / <date>"; null when the time is missing or bad. */
export function pricesPulledText(fetchedAt: unknown, now: number = Date.now()): string | null {
  if (typeof fetchedAt !== "string") return null;
  const time = new Date(fetchedAt).getTime();
  if (Number.isNaN(time)) return null;
  const days = Math.floor((now - time) / DAY_MS);
  if (days < 1) return "Prices pulled today";
  if (days === 1) return "Prices pulled yesterday";
  if (days < 7) return `Prices pulled ${days} days ago`;
  return `Prices pulled ${new Date(time).toLocaleDateString()}`;
}
